import {MultiPolygon} from "geojson";
import {feature, featureCollection} from "@turf/turf";
import dayjs, {Dayjs} from "dayjs";
import {Field} from "../types/domain/field";
import shp, {parseShp} from "shpjs";
import {v4 as uuidv4} from "uuid";
import {FIELD_INITIAL_VALUES} from "./constants";
import {CropCycle} from "../types/domain/cropCycle";
import toGeoJSON from "@mapbox/togeojson";
import {getFileExtension} from "./fileUtils";

const FIELDS_LIMIT = 3000;

export interface ShapeFileProps {
    _id: number;
    name: string;
    crop: string | number;
    irrigation: string | number;
    soil: string | number;
    pDate: Date | null;
    priority: number | null; //0|1
    ksat_bias: number | null;
    sat_soil_m: number | null;
    stress_typ: number | null;
    farmId: string | null;
    orderId: string | null;
    variety: string;

    estHarvDat: Date | null; //Estimated Harvest date
    eff_root_d: number | null; //effective Root Depth
    irr_interv: number | null; //irrigationInterval
    des_storag: number | null; //desiredMoistureLevel
    irr_crit: number | null; //irrigationCriteria
    irr_def: number | null; //irrigationDefault
}

export const parseShapeFile = async (file: File): Promise<[Field[], error: string | null]> => {
    let fields: Field[] = [];
    const fileExtension = getFileExtension(file.name).toLowerCase();
    if (fileExtension === "shp" || fileExtension === "zip") {
        try {
            const buffer = await file.arrayBuffer();
            let geojson;
            if (fileExtension === "shp") {
                const geometries = parseShp(buffer);
                const features = geometries.map((geometry) => {
                    const f = feature(geometry);
                    if (!f.properties) {
                        f.properties = {};
                    }
                    return f;
                });
                geojson = featureCollection(features);
            } else if (fileExtension === "zip") {
                geojson = await shp(buffer);
            }

            fields = convertGeoJSONToFields(geojson as shp.FeatureCollectionWithFilename);
            if (fields.length > FIELDS_LIMIT) {
                const errorMessage = `It's not allowed to create more than ${FIELDS_LIMIT} fields from one shapefile.`;
                return [[], errorMessage];
            }
            return [fields, null];
        } catch (error) {
            let errorMessage = "Error processing shapefile";
            if (error instanceof Error) {
                errorMessage = `Error processing shapefile: ${error.message}`;
            }
            return [[], errorMessage];
        }
    } else if (fileExtension === "kml") {
        // Process as KML
        const text = await file.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(text, "text/xml");
        const geojson = toGeoJSON.kml(xmlDoc);
        const fields = convertGeoJSONToFields(geojson);
        return [fields, null];
    } else {
        return [[], "Unsupported file type"];
    }
};

function valueDateOrDefault<T, K extends keyof T>(props: T, key: K, defaultValue: dayjs.Dayjs | null): Dayjs | null {
    const value = props[key];
    if (value === null || value === undefined) {
        return defaultValue;
    }
    const date = dayjs(value as string);
    //If date is not specified, the 'zero date' that is 1899 year might be return by shp lib
    if (!date.isValid() || date.year() < 1900) {
        return defaultValue;
    }
    return date;
}

export const valueOrDefaultNumber = <T, K extends keyof T>(
    props: T,
    key: K,
    defaultValue: number | null,
): number | null => {
    const value = Number(props[key]);
    //We also need to check for isNaN, and if it's NaN we return default value.
    if (Number.isNaN(value) || props[key] === null || props[key] === undefined || props[key] === "") {
        return defaultValue;
    }
    return value;
};

function valueOrDefault<T, K extends keyof T>(props: T, key: K, defaultValue: T[K]): T[K] {
    const value = props[key];
    return !Number.isNaN(value) && value ? value : defaultValue;
}

export const convertGeoJSONToFields = (geojson: shp.FeatureCollectionWithFilename) => {
    return geojson.features.map((feature) => {
        const props = feature.properties as ShapeFileProps;
        const cropCycle: CropCycle = {
            cropId: props.crop ? props.crop.toString() : FIELD_INITIAL_VALUES.cropCycles[0].cropId,
            plantedDate: valueDateOrDefault(props, "pDate", FIELD_INITIAL_VALUES.cropCycles[0].plantedDate),
            variety: valueOrDefault(props, "variety", FIELD_INITIAL_VALUES.cropCycles[0].variety),
            estHarvestDate: valueDateOrDefault(props, "estHarvDat", FIELD_INITIAL_VALUES.cropCycles[0].estHarvestDate),
            effRootDepth: valueOrDefaultNumber(props, "eff_root_d", FIELD_INITIAL_VALUES.cropCycles[0].effRootDepth),
            droughtSensitivity: valueOrDefaultNumber(
                props,
                "stress_typ",
                FIELD_INITIAL_VALUES.cropCycles[0].droughtSensitivity,
            ),
            resetCropCycle: true,
            resetETAppliedWater: true,
            resetRootDepth: true,
            actHarvestDate: null,
        };
        const parsedData: Field = {
            id: uuidv4(),
            name: props.name,

            cropCycles: [cropCycle],
            irrigationTypeId: props.irrigation ? props.irrigation.toString() : FIELD_INITIAL_VALUES.irrigationTypeId,
            soilId: props.soil ? props.soil.toString() : FIELD_INITIAL_VALUES.soilId,
            // plantedDate: props.pDate ? props.pDate.toISOString() : new Date().toISOString(),
            geometry: feature.geometry as MultiPolygon,
            farmId: props.farmId ? props.farmId : null,
            orderId: props.orderId ? props.orderId : null,

            priority: valueOrDefaultNumber(props, "priority", 0)!,
            irrigationInterval: valueOrDefaultNumber(props, "irr_interv", FIELD_INITIAL_VALUES.irrigationInterval),
            desiredMoistureLevel: valueOrDefaultNumber(props, "des_storag", FIELD_INITIAL_VALUES.desiredMoistureLevel),
            irrigationCriteria: valueOrDefaultNumber(props, "irr_crit", FIELD_INITIAL_VALUES.irrigationCriteria),
            irrigationDefault: valueOrDefaultNumber(props, "irr_def", FIELD_INITIAL_VALUES.irrigationDefault),
            satSoilMoistBias: valueOrDefaultNumber(props, "sat_soil_m", FIELD_INITIAL_VALUES.satSoilMoistBias),
            KSATBias: valueOrDefaultNumber(props, "ksat_bias", FIELD_INITIAL_VALUES.KSATBias),
        } as Field;

        // TODO: Refactor for proper utilization of DTO type instead of Domain type

        return parsedData;
    });
};
